import { KeyRound, Plus, Save, ShieldCheck, UserRoundCog } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { authFetch, type AuthUser, type PermissionAction, type UserPermission } from '../auth'

interface PageCatalogItem { pageKey: string; label: string; actions: PermissionAction[] }
interface StoreCatalogItem { storeId: string; storeName: string; pages: PageCatalogItem[] }
interface AccountsPayload { users: AuthUser[]; pageCatalog: StoreCatalogItem[] }

const actionLabels: Record<PermissionAction, string> = { view: '查看', save: '保存', edit: '编辑', upload: '上传' }
const permissionKey = (permission: UserPermission) => `${permission.storeId}|${permission.pageKey}|${permission.action}`
const permissionFromKey = (key: string): UserPermission => {
  const [storeId, pageKey, action] = key.split('|')
  return { storeId, pageKey, action: action as PermissionAction }
}

function PermissionMatrix({ catalog, selected, onChange, disabled }: { catalog: StoreCatalogItem[]; selected: Set<string>; onChange: (next: Set<string>) => void; disabled?: boolean }) {
  const toggle = (key: string) => {
    const next = new Set(selected)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    onChange(next)
  }
  const applyCalculatorTemplate = (storeId: string, pageKey: string) => {
    onChange(new Set([
      `${storeId}|${pageKey}|view`, `${storeId}|${pageKey}|save`,
    ]))
  }
  return <div className={`permission-matrix${disabled ? ' is-disabled' : ''}`}>
    {!disabled ? <div className="permission-templates"><span>快速模板</span><button type="button" onClick={() => applyCalculatorTemplate('jd-588485', 'jd.profit_calculator')}>京东利润计算器</button><button type="button" onClick={() => applyCalculatorTemplate('pdd-beijuxiong-youhe', 'pdd.profit_calculator')}>拼多多利润计算器</button></div> : null}
    {catalog.map((store) => <section key={store.storeId}><h3>{store.storeName}<small>{store.storeId}</small></h3><div className="permission-page-list">
      {store.pages.map((page) => <div className="permission-page-row" key={page.pageKey}><span><strong>{page.label}</strong><small>{page.pageKey}</small></span><div>
        {page.actions.map((action) => {
          const key = `${store.storeId}|${page.pageKey}|${action}`
          return <label key={action}><input type="checkbox" checked={selected.has(key)} disabled={disabled} onChange={() => toggle(key)} /><span>{actionLabels[action]}</span></label>
        })}
      </div></div>)}
    </div></section>)}
  </div>
}

export function AccountManagementPage() {
  const [payload, setPayload] = useState<AccountsPayload | null>(null)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [createMode, setCreateMode] = useState(false)
  const [username, setUsername] = useState('')
  const [phone, setPhone] = useState('')
  const [initialPassword, setInitialPassword] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [permissions, setPermissions] = useState<Set<string>>(new Set())
  const [resetPassword, setResetPassword] = useState('')

  const load = useCallback(async () => {
    const response = await fetch('/api/admin/accounts', { cache: 'no-store' })
    const result = await response.json() as AccountsPayload & { error?: string }
    if (!response.ok) throw new Error(result.error || '账号数据加载失败')
    setPayload(result)
    return result
  }, [])

  useEffect(() => { load().catch((reason) => setError(reason instanceof Error ? reason.message : String(reason))) }, [load])

  const selectedUser = useMemo(() => payload?.users.find((user) => user.id === selectedUserId) || null, [payload, selectedUserId])
  const editUser = (user: AuthUser) => {
    setCreateMode(false); setSelectedUserId(user.id); setUsername(user.username); setPhone(user.phone)
    setEnabled(user.enabled); setIsAdmin(user.isAdmin); setPermissions(new Set(user.permissions.map(permissionKey)))
    setInitialPassword(''); setResetPassword(''); setMessage(''); setError('')
  }
  const startCreate = () => {
    setCreateMode(true); setSelectedUserId(''); setUsername(''); setPhone(''); setInitialPassword('')
    setEnabled(true); setIsAdmin(false); setPermissions(new Set()); setResetPassword(''); setMessage(''); setError('')
  }

  const save = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError(''); setMessage('')
    try {
      const response = await authFetch(createMode ? '/api/admin/accounts' : '/api/admin/accounts/update', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(createMode ? { phone, initialPassword } : { userId: selectedUserId }),
          username, enabled, isAdmin,
          permissions: isAdmin ? [] : [...permissions].map(permissionFromKey),
        }),
      })
      const result = await response.json() as { user?: AuthUser; error?: string }
      if (!response.ok) throw new Error(result.error || '账号保存失败')
      const next = await load()
      if (result.user) editUser(next.users.find((user) => user.id === result.user?.id) || result.user)
      setMessage(createMode ? '账号已创建，首次登录必须修改初始密码。' : '账号与权限已保存。')
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)) } finally { setBusy(false) }
  }

  const performReset = async () => {
    setBusy(true); setError(''); setMessage('')
    try {
      const response = await authFetch('/api/admin/accounts/reset-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: selectedUserId, newPassword: resetPassword }),
      })
      const result = await response.json() as { error?: string }
      if (!response.ok) throw new Error(result.error || '密码重置失败')
      setResetPassword(''); setMessage('密码已重置，该账号已退出所有会话，下次登录必须修改密码。')
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)) } finally { setBusy(false) }
  }

  if (!payload && !error) return <div className="loading-state">正在加载账号与权限…</div>
  return <div className="page-stack account-admin-page">
    <header className="page-heading"><div><span className="eyebrow">系统管理</span><h1>账号与权限</h1><p>账号按手机号登录，权限按店铺、页面和操作分配；未分配的访问默认拒绝。</p></div><button className="primary-button" type="button" onClick={startCreate}><Plus aria-hidden="true" />创建账号</button></header>
    <div className="account-admin-layout">
      <aside className="account-list-panel"><h2>账号 <span>{payload?.users.length || 0}</span></h2>{payload?.users.map((user) => <button key={user.id} type="button" className={selectedUserId === user.id ? 'is-active' : ''} onClick={() => editUser(user)}><span className="account-list-avatar">{user.username.slice(0, 1)}</span><span><strong>{user.username}</strong><small>{user.phone}</small></span><em>{user.isAdmin ? '管理员' : user.enabled ? '已启用' : '已停用'}</em></button>)}</aside>
      <section className="account-editor-panel">
        {!createMode && !selectedUser ? <div className="account-empty"><UserRoundCog aria-hidden="true" /><h2>选择账号或创建新账号</h2><p>店铺运营首期可直接套用对应店铺的“利润计算器”模板。</p></div> : <form onSubmit={save}>
          <div className="account-form-heading"><div><ShieldCheck aria-hidden="true" /><span><strong>{createMode ? '创建账号' : `编辑 ${selectedUser?.username}`}</strong><small>{createMode ? '初始密码仅用于首次登录' : selectedUser?.id}</small></span></div><button className="primary-button" type="submit" disabled={busy}><Save aria-hidden="true" />{busy ? '保存中…' : '保存'}</button></div>
          <div className="account-basic-fields"><label><span>用户名</span><input value={username} onChange={(event) => setUsername(event.target.value)} maxLength={40} required /></label><label><span>手机号</span><input value={phone} onChange={(event) => setPhone(event.target.value)} disabled={!createMode} inputMode="numeric" required /></label>{createMode ? <label><span>初始密码</span><input type="password" value={initialPassword} onChange={(event) => setInitialPassword(event.target.value)} minLength={10} required /></label> : null}</div>
          <div className="account-switches"><label><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} disabled={createMode} /><span>启用账号</span></label><label><input type="checkbox" checked={isAdmin} onChange={(event) => setIsAdmin(event.target.checked)} /><span>管理员（全部店铺与页面）</span></label></div>
          <PermissionMatrix catalog={payload?.pageCatalog || []} selected={permissions} onChange={setPermissions} disabled={isAdmin} />
          {!createMode ? <div className="password-reset-panel"><div><KeyRound aria-hidden="true" /><span><strong>重置密码</strong><small>保存后立即退出该账号的所有会话</small></span></div><input type="password" value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} placeholder="输入新初始密码" minLength={10} /><button type="button" disabled={busy || resetPassword.length < 10} onClick={performReset}>重置</button></div> : null}
          {error ? <p className="form-error" role="alert">{error}</p> : null}{message ? <p className="form-success" role="status">{message}</p> : null}
        </form>}
      </section>
    </div>
  </div>
}
